import { useCallback, useSyncExternalStore } from "react";
import { FEATURE_STATUS_PENDING, FEATURE_STATUS_RERUNNING } from "config";
import { isTerminalAiRunStatus } from "utils/aiRunTransitions";
import { readBudgetRunStart } from "./useBudgetRunActive";

/*
 * Budget rerun ends, shared by the budget view's run sync and the global AI run watcher. One
 * model: a run's end (toast, budget refresh, category names) is handled at most once per run in
 * this tab, from the budget document's own status row.
 *
 * - The accepted rerun: the run this client started and the API accepted, per document, until a
 *   row ends it (its own terminal row, or a newer run's) or it expires. It outlives the budget
 *   view, so the run's end is still found on return. Not persisted: reloads and other tabs fall
 *   back to the status transitions the sync has always watched.
 * - Handled runs: whoever toasts a run's end first claims it (claimBudgetRunEnd); the other only
 *   refreshes. A replayed receipt or a remount can never toast twice.
 */

type DocumentId = string | number | null | undefined;

export type AcceptedBudgetRun = {
  runId: string | null;
  startedAt: string;
  /** The run's time limit passed with no status of its own seen. */
  expired: boolean;
  /** After expiry, the status was fetched again: it is now evaluated once, then finished. */
  checked: boolean;
};

type Entry = { run: AcceptedBudgetRun; timer: ReturnType<typeof setTimeout> };

const entries = new Map<string, Entry>();
const handledRuns = new Set<string>();
const listeners = new Set<() => void>();
const emit = () => {
  for (const listener of listeners) listener();
};
const entryKey = (projectId: string | number, documentId: DocumentId) =>
  `${projectId}:${documentId ?? ""}`;

/** A run's identity for the handled set: its id, else its start time. */
export const budgetRunKey = (run: { runId: string | null; startedAt: string }) =>
  run.runId ?? `started:${run.startedAt}`;

export const isBudgetRunHandled = (runKey: string) => handledRuns.has(runKey);
export const markBudgetRunHandled = (runKey: string) => {
  handledRuns.add(runKey);
};

const OPEN_RUN_STATUSES = new Set<string>([FEATURE_STATUS_RERUNNING, FEATURE_STATUS_PENDING]);

/**
 * Called when a rerun POST succeeds, with its response. Ignored without a readable receipt, for
 * a run the reply says is no longer running, and for a run whose end was already handled (an
 * idempotent retry replays the same receipt).
 */
export function recordAcceptedBudgetRun(
  projectId: string | number,
  documentId: DocumentId,
  response: unknown
) {
  const start = readBudgetRunStart(response);
  if (!start) return;
  if (start.status !== null && !OPEN_RUN_STATUSES.has(start.status)) return;
  if (isBudgetRunHandled(budgetRunKey(start))) return;
  const key = entryKey(projectId, documentId);
  clearTimeout(entries.get(key)?.timer);
  const run: AcceptedBudgetRun = {
    runId: start.runId,
    startedAt: start.startedAt,
    expired: false,
    checked: false,
  };
  const timer = setTimeout(() => update(key, timer, { expired: true }), start.durationMs);
  entries.set(key, { run, timer });
  emit();
}

function update(key: string, timer: Entry["timer"], change: Partial<AcceptedBudgetRun>) {
  const entry = entries.get(key);
  if (entry?.timer !== timer) return;
  entries.set(key, { run: { ...entry.run, ...change }, timer });
  emit();
}

/** After expiry, the status for this run was fetched again. */
export function markAcceptedBudgetRunChecked(
  projectId: string | number,
  documentId: DocumentId,
  run: AcceptedBudgetRun
) {
  const key = entryKey(projectId, documentId);
  const entry = entries.get(key);
  if (entry?.run.startedAt !== run.startedAt || entry.run.runId !== run.runId) return;
  update(key, entry.timer, { checked: true });
}

/** Ends the record: its end was handled, it expired, or the budget view went away. */
export function finishAcceptedBudgetRun(projectId: string | number, documentId: DocumentId) {
  const key = entryKey(projectId, documentId);
  const entry = entries.get(key);
  if (!entry) return;
  clearTimeout(entry.timer);
  entries.delete(key);
  emit();
}

/** The accepted run for this document, or null. Re-renders when it changes. */
export function useAcceptedBudgetRun(
  projectId: string | number,
  documentId: DocumentId
): AcceptedBudgetRun | null {
  const subscribe = useCallback((onChange: () => void) => {
    listeners.add(onChange);
    return () => {
      listeners.delete(onChange);
    };
  }, []);
  return useSyncExternalStore(
    subscribe,
    () => entries.get(entryKey(projectId, documentId))?.run ?? null
  );
}

type RunMetadata = { run_id?: unknown; started_at?: unknown };

/**
 * Whether a status row's metadata belongs to the accepted run: the same `run_id` (the backend
 * keeps it on every write of that run, terminal ones included), or else the same `started_at`.
 */
export function belongsToAcceptedRun(metadata: unknown, run: AcceptedBudgetRun): boolean {
  const meta = (metadata ?? {}) as RunMetadata;
  if (run.runId && typeof meta.run_id === "string") return meta.run_id === run.runId;
  return typeof meta.started_at === "string" && meta.started_at === run.startedAt;
}

/** A row from a run that started after the accepted one (someone else's newer run). */
export function isLaterRun(metadata: unknown, run: AcceptedBudgetRun): boolean {
  const startedAt = (metadata as RunMetadata | null)?.started_at;
  const rowMs = typeof startedAt === "string" ? Date.parse(startedAt) : NaN;
  return Number.isFinite(rowMs) && rowMs > Date.parse(run.startedAt);
}

/** The handled-set key for a status row: its run id, else its start time. Null if neither. */
export function budgetRunKeyOfRow(metadata: unknown): string | null {
  const meta = (metadata ?? {}) as RunMetadata;
  if (typeof meta.run_id === "string" && meta.run_id) return meta.run_id;
  return typeof meta.started_at === "string" ? `started:${meta.started_at}` : null;
}

/** The accepted run for a document, read outside React (the global watcher). */
export function readAcceptedBudgetRun(
  projectId: string | number,
  documentId: DocumentId
): AcceptedBudgetRun | null {
  return entries.get(entryKey(projectId, documentId))?.run ?? null;
}

/** How a status row ends the accepted run: its own end, a newer run's end, or not at all. */
export function acceptedRunEnd(
  run: AcceptedBudgetRun,
  status: string | undefined,
  metadata: unknown
): "own" | "newer" | null {
  if (!isTerminalAiRunStatus(status)) return null;
  if (belongsToAcceptedRun(metadata, run)) return "own";
  return isLaterRun(metadata, run) ? "newer" : null;
}

/** Whether this row ends the document's accepted run. */
export function endsAcceptedBudgetRun(
  projectId: string | number,
  documentId: DocumentId,
  status: string | undefined,
  metadata: unknown
): boolean {
  const run = readAcceptedBudgetRun(projectId, documentId);
  return Boolean(run && acceptedRunEnd(run, status, metadata));
}

/**
 * Claims a terminal budget row's end for one toast. True when this caller should toast; false
 * when the budget view or the global watcher already did (then only refresh). Either way, a row
 * that ends the document's accepted run deletes that record.
 */
export function claimBudgetRunEnd(
  projectId: string | number,
  documentId: DocumentId,
  status: string | undefined,
  metadata: unknown
): boolean {
  const run = readAcceptedBudgetRun(projectId, documentId);
  const end = run ? acceptedRunEnd(run, status, metadata) : null;
  if (end) finishAcceptedBudgetRun(projectId, documentId);
  const runKey = run && end === "own" ? budgetRunKey(run) : budgetRunKeyOfRow(metadata);
  // A row with no run identity cannot be deduplicated: keep the old one-toast-per-change rule.
  if (!runKey) return true;
  if (handledRuns.has(runKey)) return false;
  handledRuns.add(runKey);
  return true;
}

/** Tests only. */
export function resetAcceptedBudgetRuns() {
  for (const entry of entries.values()) clearTimeout(entry.timer);
  entries.clear();
  handledRuns.clear();
  emit();
}
